import { CmsData } from '@teliaee/gravity-forms';
import { action, computed, observable, runInAction } from 'mobx';
import {
  InternetCalculatorState,
  InternetCalculatorStateProps,
  InternetCategory,
  ProductCard
} from '../product/internetAndTvCalculator/InternetCalculatorInterfaces';
import { isEqualUploadAndDownloadSpeed } from '../product/internetAndTvCalculator/InternetSpeedUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import { Address, Room } from './AddressStore';
import RootStore from './RootStore';
import { Segment } from './SegmentStore';
import SsoStore from './SsoStore';

export const PLANNED_B2B_OFA_INTERNET_KEY = 'PLANEERITAV_ARIINTERNET_FIX_OFA_A_1000_1000';

class InternetCalculatorStore {
  @observable accessor selectedCategoryTelia: InternetCategory;
  @observable accessor selectedCategoryPartner: InternetCategory;
  @observable accessor calculatorStates: Map<string, InternetCalculatorState> = new Map();

  private ssoStore: SsoStore;

  constructor(rootStore: RootStore) {
    this.ssoStore = rootStore.ssoStore;
  }

  public selectCategory(category: InternetCategory, partner: boolean) {
    runInAction(() => {
      partner ? (this.selectedCategoryPartner = category) : (this.selectedCategoryTelia = category);
    });
  }

  public getState(key: string): InternetCalculatorState {
    if (!this.calculatorStates.get(key)) {
      this.createNewState(
        key,
        {
          internetCodes5G: [],
          suggestSpeed: [],
          excludedWhen5G: [],
          promotedInternets: [],
          freeRouterInternets: [],
          internetSpeedTabs: [],
          betterInternetFormName: null,
          betterInternetOnTeliaNetworkFormName: null,
          betterPartnerFormName: null,
          specialRequestFormName: null,
          partnerFormName: null,
          elionOrderPath: null,
          segment: 'PRIVATE',
          hideBetterInternetForms: false
        },
        false
      );
    }
    return this.calculatorStates.get(key);
  }

  public getSelectedInternet(key: string): ProductCard {
    return this.getState(key).selectedInternet;
  }

  public teliaProducts(key: string): ProductCard[] {
    return this.getState(key).internetOffers.teliaOffers ? this.getState(key).internetOffers.teliaOffers.slice() : [];
  }

  public hasTeliaProducts(key: string): boolean {
    return this.teliaProducts(key).length > 0;
  }

  public getTeliaProductsWithoutPlannedNetwork(key: string): ProductCard[] {
    return this.teliaProducts(key).filter((internet) => !internet.plannedNetwork);
  }

  public hasTeliaProductsWithoutPlannedNetwork(key: string) {
    return this.getTeliaProductsWithoutPlannedNetwork(key).length > 0;
  }

  public hasOnlyPlannedNetwork(key: string) {
    return this.teliaProducts(key).length === 1 && this.teliaProducts(key)[0].plannedNetwork;
  }

  public shouldShowRequestFasterInternet(key: string) {
    return (
      !this.getState(key).propState.hideBetterInternetForms &&
      !!this.getState(key).propState.betterInternetFormName &&
      !this.teliaProducts(key).find((offer) => offer.plannedNetwork) &&
      !this.getTeliaProductsWithoutPlannedNetwork(key).find((offer) => isEqualUploadAndDownloadSpeed(offer)) &&
      !this.partnerProducts(key).find((offer) => offer.plannedNetwork) &&
      !this.getPartnerProductsWithoutPlannedNetwork(key).find((offer) => isEqualUploadAndDownloadSpeed(offer))
    );
  }

  public partnerProducts(key: string): ProductCard[] {
    return this.getState(key).internetOffers.partnerOffers ? this.getState(key).internetOffers.partnerOffers.slice() : [];
  }

  public hasPartnerProducts(key: string) {
    return this.partnerProducts(key).length > 0;
  }

  public getPartnerProductsWithoutPlannedNetwork(key: string): ProductCard[] {
    return this.partnerProducts(key).filter((internet) => !internet.plannedNetwork);
  }

  public getGbitPartnerProductWithPlannedNetwork(key: string): ProductCard {
    return this.partnerProducts(key).find((internet) => internet.plannedNetwork && internet.productOfferingCode === PLANNED_B2B_OFA_INTERNET_KEY);
  }

  public getSuggestibleInternets(key: string): ProductCard[] {
    return this.getTeliaProductsWithoutPlannedNetwork(key).concat(this.getPartnerProductsWithoutPlannedNetwork(key));
  }

  public hasSuggestibleInternets(key: string) {
    return this.getSuggestibleInternets(key).length > 0;
  }

  public noResults(key: string) {
    return this.getState(key).status === FetchStatus.FETCHED && !this.hasTeliaProductsWithoutPlannedNetwork(key) && !this.hasPartnerProducts(key);
  }

  public getCmsDataStoreKey(key: string, type: string): string {
    return `internetAndTvCalculator_${key}_${type}`.replace('.', '_');
  }

  public getCmsStoreKeys(key: string) {
    return {
      addressKey: this.getCmsDataStoreKey(key, 'address'),
      roomKey: this.getCmsDataStoreKey(key, 'room'),
      addressIdKey: this.getCmsDataStoreKey(key, 'address_id'),
      buildingIdKey: this.getCmsDataStoreKey(key, 'building_id'),
      apartmentIdKey: this.getCmsDataStoreKey(key, 'apartment_id'),
      internetNameKey: this.getCmsDataStoreKey(key, 'internet_name'),
      internetTypeKey: this.getCmsDataStoreKey(key, 'internet_type'),
      plannedInternetKey: this.getCmsDataStoreKey(key, 'planned_internet')
    };
  }

  @computed
  public get teliaInternets() {
    return this.selectedCategoryTelia?.internets || [];
  }

  @computed
  public get partnerInternets() {
    return this.selectedCategoryPartner?.internets || [];
  }

  @computed
  public get selectedTeliaCategoryId() {
    return this.selectedCategoryTelia?.id || null;
  }

  @computed
  public get selectedPartnerCategoryId() {
    return this.selectedCategoryPartner?.id || null;
  }

  public isFix4GAddress(key: string) {
    return !!this.getState(key).internetOffers.teliaOffers.find((productCard) => productCard.fix4g);
  }

  public isInternet(key: string): boolean {
    return !!this.getState(key).selectedInternet;
  }

  public isCable(key: string): boolean {
    return (
      this.isInternet(key) &&
      this.getState(key).selectedInternet.productOfferingCode.search('LTE') === -1 &&
      this.getState(key).selectedInternet.productOfferingCode.search('5G') === -1 &&
      !this.getState(key).selectedInternet.fix4g
    );
  }

  public isDealerWithoutCustomer(key: string): boolean {
    return this.isCableAddress(key) && this.ssoStore.isDealer && !this.ssoStore.customerId;
  }

  public isCustomerWithoutElionCustomerId(key: string): boolean {
    return this.isCableAddress(key) && this.ssoStore.isDealer && this.ssoStore.customerId && !this.ssoStore.customer.elionCustomerId;
  }

  public shouldShowOversoldWarning(key: string) {
    const productCards = this.getTeliaProductsWithoutPlannedNetwork(key);
    return productCards.length > 0 && !productCards.find((productCard) => !productCard.liableOnlyForSalesConsultant);
  }

  @action
  public setAddress(key: string, address: Address, room: Room, inCalculator: boolean, segment: Segment = 'PRIVATE') {
    if (!address) {
      return;
    }
    const prevState = this.getState(key);
    this.createNewState(key, prevState.propState, prevState.asButton, address, room, inCalculator);
    this.setCalculatorOpen(key, true);
    return this.fetchOffersByLocation(key, address.id, room ? room.roomId : null, address.buildingId, segment);
  }

  @action
  public setInternetSuggestions(key: string, suggestedInternets: string[]) {
    this.getState(key).suggestedInternets = suggestedInternets;
  }

  @action
  public setCalculatorOpen(key: string, value: boolean, asButton: boolean = false) {
    this.getState(key).calculatorOpen = value;
    if (asButton) {
      this.getState(key).asButton = true;
    }
  }

  @action
  public setStateProps(key: string, value: InternetCalculatorStateProps) {
    this.getState(key).propState = value;
  }

  @action
  public setSelectedInternet(key: string, internet: ProductCard) {
    this.getState(key).selectedInternet = internet;
  }

  @action
  public setOrderFormOpen(key: string, val: boolean) {
    this.getState(key).orderFormOpen = val;
  }

  @action
  private fetchOffersByLocation(key: string, addressId: string, roomId: string, buildingId: string, segment: Segment) {
    const state = this.getState(key);
    const customerPath = this.ssoStore.customerId ? '/customer' : '';
    state.status = FetchStatus.FETCHING;
    return API.get(`/product/product-offering${customerPath}/location`, {
      params: { addressId, buildingId, roomId, segment },
      timeout: 30000
    })
      .then((result) => {
        runInAction(() => {
          state.internetOffers.teliaOffers = this.addFix4gExceptionProductCardIfNecessary(result.data.teliaOffers);
          state.internetOffers.partnerOffers = result.data.partnerOffers;
          state.status = FetchStatus.FETCHED;
        });
      })
      .catch(() => {
        state.status = FetchStatus.NOT_FOUND;
      });
  }

  public getCmsData = (key: string): CmsData[] => {
    const { address, room, selectedInternet } = this.calculatorStates.get(key);
    const data = [
      { key: this.getCmsDataStoreKey(key, 'address'), value: address.label },
      { key: this.getCmsDataStoreKey(key, 'room'), value: room ? room.roomName : '' },
      { key: this.getCmsDataStoreKey(key, 'address_id'), value: address.id },
      { key: this.getCmsDataStoreKey(key, 'building_id'), value: address.buildingId },
      { key: this.getCmsDataStoreKey(key, 'apartment_id'), value: room ? room.roomId : '' }
    ];

    if (selectedInternet) {
      data.push(
        { key: this.getCmsDataStoreKey(key, 'internet'), value: selectedInternet.productOfferingCode },
        { key: this.getCmsDataStoreKey(key, 'internet_name'), value: selectedInternet.name },
        { key: this.getCmsDataStoreKey(key, 'internet_type'), value: selectedInternet.partner ? 'Partner' : 'Telia' }
      );
    }

    const plannedPartnerInternet = this.getGbitPartnerProductWithPlannedNetwork(key);
    if (plannedPartnerInternet) {
      data.push({ key: this.getCmsDataStoreKey(key, 'planned_internet'), value: `Äriinternet ${plannedPartnerInternet.speed}` });
    }

    return data;
  };

  @action
  private addFix4gExceptionProductCardIfNecessary(productCards: ProductCard[]): ProductCard[] {
    const hasInternetCards = productCards.filter((internet) => !internet.plannedNetwork).length > 0;
    const hasCardsThatAreNotFix4g = productCards.some((card) => !card.fix4g);
    if (this.ssoStore.isDealer && hasInternetCards && hasCardsThatAreNotFix4g) {
      const fix4gExceptionCard: ProductCard = {
        uid: '',
        name: '',
        productOfferingCode: 'EXCEPTION_FIX4G',
        price: '',
        group: '',
        categoryCodes: [],
        fix4g: false,
        liableOnlyForSalesConsultant: false,
        plannedNetwork: false
      };
      productCards.push(fix4gExceptionCard);
    }
    return productCards;
  }

  @action
  private createNewState(
    key: string,
    prevPropState: InternetCalculatorStateProps,
    asButton: boolean,
    address?: Address,
    room?: Room,
    inCalculator: boolean = false
  ): InternetCalculatorState {
    const newState: InternetCalculatorState = {
      internetOffers: {
        teliaOffers: [],
        partnerOffers: []
      },
      status: FetchStatus.UNINITIALIZED,
      address,
      room,
      asButton,
      suggestedInternets: null,
      selectedInternet: null,
      calculatorOpen: inCalculator,
      orderFormOpen: false,
      propState: prevPropState
    };
    this.calculatorStates.set(key, newState);
    return newState;
  }

  private isCableAddress(key: string) {
    const teliaOffers = this.teliaProducts(key);
    return teliaOffers.length > 0 && teliaOffers[0].productOfferingCode.search('LTE') === -1 && !teliaOffers[0].fix4g;
  }
}

export default InternetCalculatorStore;
